/**
 * The spot a page opens at (issue #3573): an Ask Forged card for something
 * waiting on the founder opens its page at that thing, through `?spot=<id>`.
 *
 *   - An action item's card names the item, and the build page opens at its
 *     row while the row says Stuck or Needs you.
 *   - A plan's or a task's card names the clarify question waiting on the
 *     founder, else its first open choice (the citation's `params.spot`,
 *     store/assistant_citations.go), and the plan page opens at that
 *     question, the task page at that choice, while it still waits.
 *
 * The route reads the id (validateSpot) and hands it down through
 * SpotProvider. The thing it names claims it with useSpotTarget on the draw
 * the spot arrives on, and only while it still waits. A stale spot (the
 * question was answered, the item was rerun, the choice was made) is claimed
 * by nothing, so the page opens at the top as it always does, with no error.
 * Each spot is decided once, when it arrives: a row that starts waiting
 * later, while the founder reads the page, never moves it. A new spot on a
 * page already open (a second card for the same feature) is a new arrival,
 * and is decided again.
 *
 * Scrolling is smooth, and a jump under reduced motion, the rule
 * CurrentStepLine and the Layouts page follow. Focus moves to the target
 * without a second scroll, so a keyboard or screen reader user starts there.
 * While the page settles, the target is kept in view as sections above it
 * draw (keepInViewWhileSettling). Build, task and plan pages only.
 */
import { createContext, useContext, useEffect, useRef, useState, type ReactNode, type RefObject } from "react";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A route's `spot` search parameter: kept only when it is a UUID, lower
 *  cased, so a mangled link opens like a link without it. */
export function validateSpot(raw: unknown): string | undefined {
  return typeof raw === "string" && UUID.test(raw) ? raw.toLowerCase() : undefined;
}

const SpotContext = createContext<string | undefined>(undefined);

/** Hands the route's spot to whatever on the page it names. */
export function SpotProvider({ spot, children }: { spot: string | undefined; children: ReactNode }) {
  return <SpotContext.Provider value={spot}>{children}</SpotContext.Provider>;
}

/** Smooth, or a jump under reduced motion. */
function spotScrollBehavior(): ScrollBehavior {
  return (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false) ? "auto" : "smooth";
}

/** The longest the page keeps the spot in view while it settles. Its slowest
 *  first read, the build page's Changes card, asks GitHub, and the server
 *  gives each GitHub call up to 30 seconds (Codex on PR for #3573). */
const SPOT_SETTLE_MS = 120_000;

/** What the founder does to move the page themselves. */
const FOUNDER_MOVES = ["wheel", "touchstart", "keydown", "pointerdown"] as const;

/**
 * Issue #3573 (Research's retest, Oct 19, 2026): the page goes on drawing
 * after the spot is scrolled to. The build page's Changes card, above the
 * action items, shows its loader and then GitHub's file list a moment later,
 * and that pushed the failed step's row below the window. So, until the page
 * settles, whenever something above the target moves it, it is scrolled to
 * again. Moves below it do not count.
 *
 * It stops as soon as the founder moves the page themselves, so the page
 * never moves under their hand: a scroll, key or tap, or a scroll that takes
 * the target further from the middle of the window, which is how a scrollbar
 * drag or Find in page shows (neither sends the page an input event). The
 * page's own scrolls only ever bring the target closer. It stops too when
 * focus leaves the target, or after SPOT_SETTLE_MS.
 */
function keepInViewWhileSettling(target: HTMLElement, scrollToTarget: () => void): () => void {
  if (typeof ResizeObserver === "undefined") return () => {};
  const placed = () => target.getBoundingClientRect().top + window.scrollY;
  /** How far the target's middle is from the window's middle. */
  const offCentre = () => {
    const box = target.getBoundingClientRect();
    return Math.abs(box.top + box.height / 2 - window.innerHeight / 2);
  };
  let at = placed();
  // The document grows or shrinks whenever a section above draws.
  const observer = new ResizeObserver(() => {
    if (!target.isConnected) return;
    const now = placed();
    if (Math.abs(now - at) < 1) return;
    at = now;
    scrollToTarget();
  });
  observer.observe(document.documentElement);
  let seenAt = at;
  let seenOff = offCentre();
  const onScroll = () => {
    const now = placed();
    const off = offCentre();
    // The layout moved the target since the last scroll: the observer
    // answers that, and the distance says nothing about the founder.
    const layoutMoved = Math.abs(now - seenAt) >= 1;
    const away = off > seenOff + 1;
    seenAt = now;
    seenOff = off;
    if (!layoutMoved && away) stop();
  };
  const stop = () => {
    observer.disconnect();
    clearTimeout(timer);
    for (const type of FOUNDER_MOVES) window.removeEventListener(type, stop, true);
    window.removeEventListener("scroll", onScroll);
    target.removeEventListener("blur", stop);
  };
  const timer = setTimeout(stop, SPOT_SETTLE_MS);
  for (const type of FOUNDER_MOVES) window.addEventListener(type, stop, { capture: true, passive: true });
  window.addEventListener("scroll", onScroll, { passive: true });
  target.addEventListener("blur", stop);
  return stop;
}

/** What a target spreads on its element: a ref, and, once it has claimed
 *  the spot, what lets it hold focus and be named. */
export interface SpotTargetProps<T extends HTMLElement> {
  ref: RefObject<T | null>;
  tabIndex?: -1;
  role?: "group";
  "aria-labelledby"?: string;
}

/**
 * Claims the spot for the element its props are spread on, when the page's
 * spot is `id` and the thing still waits (`live`) on the draw the spot
 * arrives on. The claimed element is scrolled to and focused once per
 * arrival. It keeps `tabIndex` -1 so
 * it can hold focus, and is a group named by `labelledBy`, the id of the
 * text it already shows (the question, or the row's title), so a screen
 * reader says where focus landed. Anything else gets only an inert ref.
 */
export function useSpotTarget<T extends HTMLElement>(id: string, live: boolean, labelledBy: string): SpotTargetProps<T> {
  const spot = useContext(SpotContext);
  const ref = useRef<T | null>(null);
  const claims = (arrived: string | undefined) => arrived !== undefined && live && arrived === id.toLowerCase();
  // The spot last decided on, and what was decided. A different spot is a
  // new arrival, decided during the render that sees it; the same spot is
  // never decided again, so a later change to `live` cannot move the page.
  const [decided, setDecided] = useState(() => ({ spot, claimed: claims(spot) }));
  let current = decided;
  if (decided.spot !== spot) {
    current = { spot, claimed: claims(spot) };
    setDecided(current);
  }
  useEffect(() => {
    if (!current.claimed) return;
    const target = ref.current;
    if (!target) return;
    const behavior = spotScrollBehavior();
    const scrollToTarget = () => target.scrollIntoView?.({ behavior, block: "center" });
    scrollToTarget();
    target.focus({ preventScroll: true });
    return keepInViewWhileSettling(target, scrollToTarget);
  }, [current]);
  return current.claimed ? { ref, tabIndex: -1, role: "group", "aria-labelledby": labelledBy } : { ref };
}
